import React from 'react';
import {Button, RadioButton, Switch} from 'web-components';
import {BUDGET_TYPE, TASK_COST_CALCULATION_TYPE} from '../../../constants';
import {CheckBoxDescription, RadioWithDescriptionWrapper, SelectionElementLabel, SelectionElementLineWrapper, SubSection} from './ProjectSettings_styled';
import {changeTaskCostCalculationType, changeUnassignedTaskHourlyCost, changeUseBaseline} from './ProjectSettingsMutations';
import {FormattedHTMLMessage} from 'react-intl';
import {CurrencyInputWrapper} from '../../../components/inputs/currency_input_wrapper';
import Util from '../../../forecast-app/shared/util/util';
import ForecastTooltip from '../../../forecast-app/shared/components/tooltips/ForecastTooltip';
import RateCardDropdown, {changeRateCard} from '../../../components/dropdowns/rate-card-dropdown/Rate_card_dropdown';

export const TASK_MONITORING = {
	NO_TASK_MONITORING: 'no_monitoring',
	USE_FOLLOWERS: 'followers',
	USE_OWNER: 'owner',
};

const ProjectSettingsFinancials = ({
	intl,
	projectId,
	useBaseline,
	showChangeBudgetTypeModal,
	hasFinanceAccess,
	hasBaselineAccess,
	taskCostCalculationType,
	unassignedTaskHourlyCost,
	currency,
	projectDoneOrHalted,
	hasLockedPeriod,
	budgetType,
	hasFixedPriceV2,
	showUpdateFixedPriceModal,
	rateCard,
	company,
	project,
	availableFeatureFlags,
}) => {
	const {formatMessage} = intl;
	const isDoneOrHalted = project.status === 'DONE' || project.status === 'HALTED';
	return (
		<>
			{/** Baseline */}
			{budgetType !== BUDGET_TYPE.RETAINER && (
				<SubSection title={formatMessage({id: 'project_section.baseline'})} showCrown={!hasBaselineAccess} description={formatMessage({id: 'project_settings.baseline_desc'})}>
					<SelectionElementLineWrapper>
						<Switch checked={useBaseline} onChange={value => changeUseBaseline(projectId, value)} locked={projectDoneOrHalted || !hasBaselineAccess} />
						<SelectionElementLabel>{formatMessage({id: 'common.enable'})}</SelectionElementLabel>
					</SelectionElementLineWrapper>
				</SubSection>
			)}
			{/** budget type */}
			<SubSection title={formatMessage({id: 'project_settings.project_budget_type'})}>
				{Util.getBudgetTypeTranslation(budgetType, intl)}
				<SelectionElementLineWrapper>
					<ForecastTooltip content={hasLockedPeriod && formatMessage({id: 'retainer.locked_period_disable'})}>
						<Button disabled={hasLockedPeriod} size={Button.SIZE.STANDARD} variant={Button.VARIANT.VERY_LIGHT_GRAY_OUTLINE} onClick={showChangeBudgetTypeModal}>
							{formatMessage({id: 'project_settings.change_budget_type'})}
						</Button>
					</ForecastTooltip>
				</SelectionElementLineWrapper>
			</SubSection>
			{/** rate card */}
			<SubSection title={formatMessage({id: 'settings.rate-card'})}>
				<div style={{width: '250px'}}>
					<RateCardDropdown
						rateCards={company.rateCards.edges}
						company={company}
						project={project}
						selectedItems={rateCard && rateCard.id ? [rateCard.id] : []}
						dropdownAlignment="none"
						onSelect={values => changeRateCard(projectId, values[0])}
						onRemove={() => false}
						disabled={isDoneOrHalted}
					/>
				</div>
			</SubSection>
			{/** Fixed Price V2 Upgrade */}
			{hasFixedPriceV2 && budgetType === BUDGET_TYPE.FIXED_PRICE ? (
				<SubSection
					title={formatMessage({id: 'project_budget.new_fixed_price_logic'})}
					actionElement={
						<Button size={Button.SIZE.STANDARD} variant={Button.VARIANT.PURPLE_OUTLINE} onClick={showUpdateFixedPriceModal}>
							{formatMessage({id: 'project_budget.update_fixed_price'})}
						</Button>
					}
				/>
			) : null}
			{/** Internal hourly cost calculation */}
			{hasFinanceAccess && (
				<SubSection title={formatMessage({id: 'project_settings.internal_hourly_cost_calculation'})} description={formatMessage({id: 'project_settings.internal_hourly_cost_calculation_desc'})}>
					<RadioWithDescriptionWrapper>
						<SelectionElementLineWrapper>
							<RadioButton
								checked={taskCostCalculationType === TASK_COST_CALCULATION_TYPE.COMPANY_AVERAGE_UNASSIGNED_TASKS}
								onClick={() => changeTaskCostCalculationType(projectId, TASK_COST_CALCULATION_TYPE.COMPANY_AVERAGE_UNASSIGNED_TASKS)}
								disabled={projectDoneOrHalted}
							/>
							<SelectionElementLabel>
								<FormattedHTMLMessage id={'project_settings.company_averages'} />
							</SelectionElementLabel>
						</SelectionElementLineWrapper>
						<CheckBoxDescription>{formatMessage({id: 'project_settings.company_averages_desc'})}</CheckBoxDescription>
					</RadioWithDescriptionWrapper>
					<RadioWithDescriptionWrapper>
						<SelectionElementLineWrapper>
							<RadioButton
								checked={taskCostCalculationType === TASK_COST_CALCULATION_TYPE.PROJECT_TEAM_AVERAGE_UNASSIGNED_TASKS}
								onClick={() => changeTaskCostCalculationType(projectId, TASK_COST_CALCULATION_TYPE.PROJECT_TEAM_AVERAGE_UNASSIGNED_TASKS)}
								disabled={projectDoneOrHalted}
							/>
							<SelectionElementLabel>
								<FormattedHTMLMessage id={'project_settings.project_team_averages'} />
							</SelectionElementLabel>
						</SelectionElementLineWrapper>
						<CheckBoxDescription>{formatMessage({id: 'project_settings.project_team_averages_desc'})}</CheckBoxDescription>
					</RadioWithDescriptionWrapper>
					<RadioWithDescriptionWrapper>
						<SelectionElementLineWrapper>
							<RadioButton
								checked={taskCostCalculationType === TASK_COST_CALCULATION_TYPE.FIXED_UNASSIGNED_TASKS}
								onClick={() => changeTaskCostCalculationType(projectId, TASK_COST_CALCULATION_TYPE.FIXED_UNASSIGNED_TASKS)}
								disabled={projectDoneOrHalted}
							/>
							<SelectionElementLabel>
								<FormattedHTMLMessage id={'project_settings.set_fixed_internal_cost'} />
							</SelectionElementLabel>
						</SelectionElementLineWrapper>
						<CheckBoxDescription>
							<CurrencyInputWrapper
								intl={intl}
								value={unassignedTaskHourlyCost}
								callback={value => changeUnassignedTaskHourlyCost(projectId, value)}
								currency={currency}
								width={'130px'}
								disabled={projectDoneOrHalted || taskCostCalculationType !== TASK_COST_CALCULATION_TYPE.FIXED_UNASSIGNED_TASKS}
							/>
						</CheckBoxDescription>
					</RadioWithDescriptionWrapper>
				</SubSection>
			)}
		</>
	);
};
export default ProjectSettingsFinancials;
